import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAutoScroll } from '@/features/chat/shared/hooks/useAutoScroll';
import { ChatContainer } from '@/features/chat/shared/components/ChatContainer';
import { ScrollableMessageArea } from '@/features/chat/shared/components/ScrollableMessageArea';
import { cn } from '../../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
} from '../../../components/ui/collapsible';

import ChatInput, { type ChatInputRef } from './ChatInput';
import AIChatHistory from './history';
import ChatSuggestions from './ChatSuggestions';
import ChatEmpty from './ChatEmpty';
import TranscriptLimitReached from '../../selectedVideo/transcriptLimitReached/Right';
import { RootState } from '../../../reducers';
import useLimits from '../../plan/hooks/limits';
import {
  deleteAskAISession,
  setVideoMode,
} from '../../selectedVideo/selectedVideoSlice';

interface ChatProps {
  isInTab?: boolean;
  isInPanel?: boolean;
}

export interface SingleFileChatRef {
  clearChat: () => void;
  hasContent: () => boolean;
  focusInput: () => void;
}

const SingleFileChat = forwardRef<SingleFileChatRef, ChatProps>(
  ({ isInTab = false, isInPanel = false }, ref) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { selectedVideo } = useSelector((state: RootState) => state);
    const { conversation, sessionId, status } = selectedVideo.chat;
    const { textData, streams, _id } = selectedVideo.file || {};
    const { transcriptUrl } = textData || {};
    const isTranscriptAvailable = !!transcriptUrl;
    const guardLimits = useLimits();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const chatInputRef = React.useRef<ChatInputRef>(null);

    // Use shared auto-scroll hook
    const { containerRef, scrollAnchorRef } = useAutoScroll({
      dependencies: [conversation.length],
      enabled: true,
      isStreaming: status === 'pending',
    });

    // Auto-enable video analysis when there's no audio and no transcript
    useEffect(() => {
      if (selectedVideo.status !== 'succeeded') return;
      const { isVideoMode } = selectedVideo.chat;
      const isVideoOnly =
        streams?.includes('video') && !streams?.includes('audio');

      // If video-only (no audio) and video mode is not already enabled, enable it automatically
      if (isVideoOnly && !isTranscriptAvailable && !isVideoMode) {
        dispatch(setVideoMode(true));
      }
    }, [
      streams,
      isTranscriptAvailable,
      selectedVideo.chat.isVideoMode,
      selectedVideo.status,
      dispatch,
    ]);

    const handleDeleteConfirm = () => {
      setIsDeleteDialogOpen(false);
      dispatch(
        deleteAskAISession({
          fileId: _id,
          sessionId,
        }),
      );
    };

    // Expose clearChat, hasContent, and focusInput methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        clearChat: () => setIsDeleteDialogOpen(true),
        hasContent: () => conversation.length > 0,
        focusInput: () => {
          chatInputRef.current?.focusInput();
        },
      }),
      [conversation.length],
    );

    if (selectedVideo.status !== 'succeeded') {
      return null;
    }

    const renderContent = () => {
      if (!isTranscriptAvailable && !(guardLimits('transcription') > 0)) {
        return <TranscriptLimitReached />;
      }

      if (!isTranscriptAvailable && !streams?.includes('audio')) {
        const { isVideoMode, ocrEnabled } = selectedVideo.chat;
        return (
          <>
            <ChatContainer.Messages scrollVariant="flex-reverse">
              <ScrollableMessageArea
                containerRef={containerRef}
                scrollAnchorRef={scrollAnchorRef}
              >
                <Collapsible open={!isVideoMode && !ocrEnabled}>
                  <CollapsibleContent
                    className={cn('transition-all duration-200')}
                  >
                    <div className="text-center mb-16">
                      <h5 className="mb-4 font-medium text-xl">
                        {t('chat.noAudioMessage')}
                      </h5>
                      <p className="text-muted-foreground">
                        {t('chat.enableMessage')}
                      </p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
                <AIChatHistory />
              </ScrollableMessageArea>
            </ChatContainer.Messages>
            {isInTab && <div className="flex-1" />}
            <ChatContainer.Input>
              <div className="px-3 pb-3">
                <ChatInput ref={chatInputRef} noAudio />
              </div>
            </ChatContainer.Input>
          </>
        );
      }

      if (!isTranscriptAvailable) {
        return (
          <>
            <ChatContainer.Messages>
              <ChatEmpty />
            </ChatContainer.Messages>
            <ChatContainer.Input>
              <ChatSuggestions />
              <div className="px-3 pb-3">
                <ChatInput ref={chatInputRef} />
              </div>
            </ChatContainer.Input>
          </>
        );
      }

      return (
        <>
          <ChatContainer.Messages scrollVariant="flex-reverse">
            <ScrollableMessageArea
              containerRef={containerRef}
              scrollAnchorRef={scrollAnchorRef}
            >
              <AIChatHistory />
            </ScrollableMessageArea>
          </ChatContainer.Messages>
          {isInTab && <div className="flex-1" />}
          <ChatContainer.Input>
            <ChatSuggestions />
            <div className="px-3 pb-3">
              <ChatInput ref={chatInputRef} />
            </div>
          </ChatContainer.Input>
          <Dialog
            open={isDeleteDialogOpen}
            onOpenChange={(open) => setIsDeleteDialogOpen(open)}
          >
            <DialogContent
              aria-labelledby="delete-chat-dialog-title"
              aria-describedby="delete-chat-dialog-description"
            >
              <DialogHeader>
                <DialogTitle id="delete-chat-dialog-title">
                  {t('chat.clearHistory.title')}
                </DialogTitle>
              </DialogHeader>
              <p
                id="delete-chat-dialog-description"
                className="text-sm text-muted-foreground"
              >
                {t('chat.clearHistory.message')}
              </p>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsDeleteDialogOpen(false)}
                >
                  {t('chat.clearHistory.keep')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteConfirm}
                  autoFocus
                >
                  {t('chat.clearHistory.clear')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      );
    };

    return (
      <ChatContainer layout="card" isInPanel={isInPanel} isInTab={isInTab}>
        {renderContent()}
      </ChatContainer>
    );
  },
);

SingleFileChat.displayName = 'SingleFileChat';

export default SingleFileChat;
